import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import './ag-page-sticky.css';

/** Approved, AG Grid 36-specific layout exception. Move native containers, never clone controls. */
export function attachPageSticky(host: HTMLElement): () => void {
  const viewport = host.querySelector<HTMLElement>('.ag-grid-viewport');
  const top = host.querySelector<HTMLElement>('.ag-grid-pinned-top-rows');
  const bottom = host.querySelector<HTMLElement>('.ag-grid-pinned-bottom-rows');
  if (!viewport || !top || !bottom)
    throw new CopyError(viewerStrings.webComponentsAgPageSticky.pageStickyLayoutRequiresTheAGGrid);
  host.classList.add('ag-page-sticky');
  let frame = 0;
  let disposed = false;
  const update = () => {
    frame = 0;
    if (disposed) return;
    const bounds = viewport.getBoundingClientRect();
    const topHeight = top.offsetHeight;
    const bottomHeight = bottom.offsetHeight;
    const travel = Math.max(0, bounds.height - topHeight - bottomHeight);
    const topShift = Math.min(travel, Math.max(0, -bounds.top));
    // Keep the two frozen regions separate even in a short browser window.
    const bottomTarget = Math.max(
      bounds.top + topShift + topHeight,
      window.innerHeight - bottomHeight,
    );
    const bottomShift = bottomHeight
      ? Math.max(-travel, Math.min(0, bottomTarget - (bounds.bottom - bottomHeight)))
      : 0;
    host.style.setProperty('--lab-frozen-top-shift', `${topShift}px`);
    host.style.setProperty('--lab-frozen-bottom-shift', `${bottomShift}px`);
  };
  const schedule = () => {
    if (!disposed && !frame) frame = requestAnimationFrame(update);
  };
  const observer = new ResizeObserver(schedule);
  for (const element of [viewport, top, bottom, document.body]) observer.observe(element);
  // Passive observation only: preserve AG Grid's own horizontal scrolling and input events.
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
  return () => {
    disposed = true;
    observer.disconnect();
    document.removeEventListener('scroll', schedule, true);
    window.removeEventListener('resize', schedule);
    cancelAnimationFrame(frame);
    host.classList.remove('ag-page-sticky');
    host.style.removeProperty('--lab-frozen-top-shift');
    host.style.removeProperty('--lab-frozen-bottom-shift');
  };
}
